import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, Users, Calendar, TrendingUp, AlertTriangle, Package, FileText, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import ServiceTasksPanel from '../components/ServiceTasksPanel';

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente statistikk');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Laster...</div>
      </div>
    );
  }

  const pageAccess = user?.page_access;
  const hasAccess = (path) => !pageAccess || pageAccess.length === 0 || pageAccess.includes(path);

  const quickActions = [
    { path: '/customers', label: 'Se alle kunder', icon: Users, testId: 'view-all-customers-button' },
    { path: '/cleaning', label: 'Rens planlegging', icon: Calendar, testId: 'view-cleaning-schedule-button' },
    { path: '/montering', label: 'Montering', icon: Wrench, testId: 'quick-montering-button' },
    { path: '/loaned-equipment', label: 'Utlånt utstyr', icon: Package, testId: 'quick-loaned-button' },
    { path: '/reports', label: 'Rapporter', icon: FileText, testId: 'quick-reports-button' },
  ];

  const statCards = [
    {
      title: 'Akutte Oppdrag',
      value: stats?.urgent_tasks || 0,
      icon: AlertTriangle,
      color: 'bg-orange-500',
      description: 'Krever umiddelbar handling',
      action: () => document.getElementById('tasks-section')?.scrollIntoView({ behavior: 'smooth' }),
      testId: 'urgent-tasks-card',
      pages: ['/'],
    },
    {
      title: 'Totalt Kunder',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'bg-brand-500',
      description: 'Aktive kunder',
      action: () => navigate('/customers'),
      testId: 'total-customers-card',
      pages: ['/customers'],
    },
    {
      title: 'Forfalt',
      value: stats?.overdue || 0,
      icon: AlertCircle,
      color: 'bg-red-500',
      description: 'Krever akutt vedlikehold',
      action: () => navigate('/cleaning?status=overdue'),
      testId: 'overdue-card',
      pages: ['/cleaning'],
    },
    {
      title: 'Denne Uken',
      value: stats?.due_this_week || 0,
      icon: Calendar,
      color: 'bg-emerald-500',
      description: 'Må renses nå',
      action: () => navigate('/cleaning?status=due'),
      testId: 'due-this-week-card',
      pages: ['/cleaning'],
    },
    {
      title: 'Kommende',
      value: stats?.upcoming || 0,
      icon: TrendingUp,
      color: 'bg-slate-500',
      description: 'Planlagt snart',
      action: () => navigate('/cleaning?status=upcoming'),
      testId: 'upcoming-card',
      pages: ['/cleaning'],
    },
  ];

  const filteredStatCards = statCards.filter(card => card.pages.some(p => hasAccess(p)));
  const showServiceTasks = hasAccess('/');

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Dashboard
        </h1>
        <p className="text-slate-600">Oversikt over service og vedlikehold</p>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Hurtighandlinger</CardTitle>
          <CardDescription>Vanlige oppgaver</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {quickActions.filter(qa => !pageAccess || pageAccess.length === 0 || pageAccess.includes(qa.path)).map(qa => (
            <Button key={qa.path} data-testid={qa.testId} className="w-full justify-start btn-secondary" variant="outline" onClick={() => navigate(qa.path)}>
              <qa.icon className="mr-2 h-4 w-4" />
              {qa.label}
            </Button>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {filteredStatCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card
              key={card.title}
              data-testid={card.testId}
              className="stat-card cursor-pointer hover:shadow-md transition-shadow"
              onClick={card.action}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                <CardTitle className="text-sm font-medium text-slate-600">
                  {card.title}
                </CardTitle>
                <div className={`${card.color} p-2 rounded-lg`}>
                  <Icon className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {card.value}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {card.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {showServiceTasks && (
        <div className="mb-8">
          <ServiceTasksPanel onTasksChanged={fetchStats} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
